import { z } from "zod";
import { MARK_DECIMALS, MARK_LIMIT } from "./competition/marks";
import type { EventQualifyingStandard } from "./competition/reference";
import type { CompetitorType, EventGender, MemberRole, ResultType } from "./competition/vocab";
import { PERSON_GENDERS, type PersonGender } from "./competition/participants";
import { PAGE_SIZE_MAX, paginationQuery, searchParam } from "./pagination";

/**
 * M7a Shortlist (migration 0035, user decisions 2026-10-16). Wire format:
 * snake_case. The NOC (or the host for it) picks, per athlete per event,
 * PRIMARY / RESERVE / not entered. OVR keeps building entries and teams, but
 * its pickers and roster writes accept only people shortlisted for THAT event
 * (422 NOT_SHORTLISTED).
 *
 * Who can be shortlisted (the DB guard `fn_shortlist_entry_guard`, psql
 * included, raises a named 23514 → SHORTLIST_NOT_ELIGIBLE):
 *   · the card's category is `ATHLETE_CATEGORY_CODE` (`main_functions.code`,
 *     case-blind);
 *   · the card is APPROVED when the row is written (INSERT, or a role / event
 *     change). A card that later leaves APPROVED keeps its rows, but they
 *     stop counting: every read joins `status = 'APPROVED'`;
 *   · the event's sport is the card's `sport_id` or one of its `sport_ids`,
 *     and the event is of the card's games.
 *
 * Quota (decided by the writing statement, event row FOR UPDATE):
 * `shortlistPrimaryLimit` below. RESERVE is unlimited. Un-shortlisting a
 * person who already holds an OVR entry for the event is 409
 * SHORTLIST_ENTERED (also a DB guard on DELETE).
 *
 * No "final entries" step (user, 2026-10-31): the shortlist has a DEADLINE,
 * an `entry_windows` row of phase SHORTLIST (`ENTRY_WINDOW_PHASES`, entries.ts)
 * for the games and the organisation or every organisation. No matching
 * window = open, no deadline; windows matching = scoped logins may write only
 * while one covers now (refused row by row, `deadline_passed`). ADMIN /
 * SUPER_ADMIN always edit. Moving the window later reopens editing. Entries
 * count at once, whatever the deadline.
 */

// ── Vocabulary ──────────────────────────────────────────────────────────────

export const SHORTLIST_ROLES = ["PRIMARY", "RESERVE"] as const;
export type ShortlistRole = (typeof SHORTLIST_ROLES)[number];

/**
 * The roster roles the OVR gate applies to (coordinator, 2026-10-16): an
 * ATHLETE or RESERVE member must be shortlisted for the event by the
 * competitor's organisation (422 NOT_SHORTLISTED). GUIDE, PILOT and COX are
 * support people (often not athlete cards) and stay enterable as before.
 */
export const SHORTLISTED_MEMBER_ROLES = ["ATHLETE", "RESERVE"] as const satisfies readonly MemberRole[];
export const memberNeedsShortlist = (role: MemberRole): boolean => (SHORTLISTED_MEMBER_ROLES as readonly MemberRole[]).includes(role);

/** The category (`main_functions.main_function_code`) of an athlete's card. Compared case-blind, like the code's unique index. */
export const ATHLETE_CATEGORY_CODE = "Aa";

/** `PUT /shortlist/entries`: rows per call. */
export const SHORTLIST_ENTRIES_BATCH_MAX = 200;
/** A best record's competition ("SEA Age Group Championships 2026"), trimmed (0062, CHECK). */
export const BEST_MARK_COMPETITION_MAX = 120;

/**
 * Where one organisation's shortlist deadline stands (`shortlistDeadlineState`
 * of a `ShortlistDeadline`), by the database clock:
 *   none      no SHORTLIST window matches — open, no deadline;
 *   open      a matching window covers now — open until `closes_at`;
 *   upcoming  windows match, none covers now, one is still to open (`next_opens_at`) — closed until then;
 *   closed    windows match and every one has ended — the deadline (`closes_at`) passed.
 */
export const SHORTLIST_DEADLINE_STATES = ["none", "open", "upcoming", "closed"] as const;
export type ShortlistDeadlineState = (typeof SHORTLIST_DEADLINE_STATES)[number];

/**
 * Why the DB guard refused an entry (23514 constraint → `details.reason` of
 * SHORTLIST_NOT_ELIGIBLE):
 *   category      the card is not an athlete's (`ATHLETE_CATEGORY_CODE`)
 *   not_approved  the card is not APPROVED
 *   sport         the event is not of the card's sport(s)
 *   games         the event is of another games
 *   gender        a MEN event needs a MALE person, a WOMEN event a FEMALE one
 *                 (MIXED / OPEN: anyone; no gender on record: refused) — 0041
 */
export const SHORTLIST_INELIGIBLE_REASONS = ["category", "not_approved", "sport", "games", "gender"] as const;
export type ShortlistIneligibleReason = (typeof SHORTLIST_INELIGIBLE_REASONS)[number];

/** The guard's constraint names, one per reason (kept equal to its latest definition, 0041, by contract-parity.shortlist-eacc.test.ts). */
export const SHORTLIST_INELIGIBLE_CONSTRAINTS = {
	ck_shortlist_entries_category: "category",
	ck_shortlist_entries_card_approved: "not_approved",
	ck_shortlist_entries_sport: "sport",
	ck_shortlist_entries_games: "games",
	ck_shortlist_entries_gender: "gender",
} as const satisfies Record<string, ShortlistIneligibleReason>;

/** The DELETE guard: the person holds an OVR entry for the event (→ SHORTLIST_ENTERED). */
export const SHORTLIST_ENTERED_CONSTRAINT = "ck_shortlist_entries_entered";

/**
 * Why one row of a `PUT /shortlist/entries` batch was left alone:
 *   not_in_scope   the caller may not write this card (`cardWritable(actor, 'competition.shortlist')`), or the card or the event does not exist
 *   not_eligible   SHORTLIST_NOT_ELIGIBLE for that row (`ineligible` says why)
 *   quota_full     PRIMARY would exceed the event's limit for the organisation (`limit`, `used`)
 *   deadline_passed the organisation's shortlist is closed — SHORTLIST windows match and none
 *                  covers now (the deadline passed, or the window has not opened yet) — and the
 *                  caller is not ADMIN / SUPER_ADMIN
 *   entered        clearing the row of a person who holds an OVR entry for the event
 *   mark_required  (0062) PRIMARY in an event with a qualifying standard, and no best record
 *   below_standard (0062) PRIMARY with a best record that does not meet the standard
 *                  (`meetsStandard`, the event's `lower_is_better`)
 * `mark_required` / `below_standard` bind scoped logins only: RESERVE needs no
 * record, and ADMIN / SUPER_ADMIN save anyway (the row then reads
 * `meets_standard: false`).
 */
export const SHORTLIST_REFUSAL_REASONS = ["not_in_scope", "not_eligible", "quota_full", "deadline_passed", "entered", "mark_required", "below_standard"] as const;
export type ShortlistRefusalReason = (typeof SHORTLIST_REFUSAL_REASONS)[number];

/**
 * How many PRIMARY athletes one organisation may shortlist in an event; null
 * = no limit. The event's own shortlist quota `max_shortlist_per_noc` (0062)
 * when set; else INDIVIDUAL: `max_entries_per_noc`, TEAM: `max_entries_per_noc
 * × team_size_max` when both are set, else no limit. The API computes the
 * same in SQL (`primaryLimitSql`, the writing statement; pinned by
 * contract-parity); this is the display mirror.
 */
export function shortlistPrimaryLimit(event: {
	competitor_type: CompetitorType;
	max_entries_per_noc: number | null;
	max_shortlist_per_noc: number | null;
	team_size_max: number | null;
}): number | null {
	if (event.max_shortlist_per_noc !== null) return event.max_shortlist_per_noc;
	if (event.max_entries_per_noc === null) return null;
	if (event.competitor_type === "INDIVIDUAL") return event.max_entries_per_noc;
	return event.team_size_max === null ? null : event.max_entries_per_noc * event.team_size_max;
}

// ── Queries ─────────────────────────────────────────────────────────────────

const uuid = z.string().uuid();

/**
 * `GET /shortlist/sports` and `GET /shortlist/sports/:sportId`. A scoped login
 * may omit `responsible_organization_id` when its scope holds exactly one
 * organisation; ADMIN / SUPER_ADMIN pick one (422 without).
 */
export const shortlistQuery = z.object({
	games_id: uuid,
	responsible_organization_id: uuid.optional(),
});
export type ShortlistQuery = z.infer<typeof shortlistQuery>;
export type ShortlistQueryInput = z.input<typeof shortlistQuery>;

export const shortlistSportParams = z.object({ sportId: uuid });

/**
 * Where one organisation stands (one state each, in this order):
 *   closed       its deadline passed (`shortlistDeadlineState` = closed), whatever it entered;
 *   not_started  no entry yet;
 *   in_progress  anything else.
 */
export const SHORTLIST_ORG_STATES = ["not_started", "in_progress", "closed"] as const;
export type ShortlistOrgState = (typeof SHORTLIST_ORG_STATES)[number];

/**
 * `GET /shortlist/organizations` — the NOCs the caller may read, with their
 * shortlist progress. Offset pages: one games' NOCs are a short bounded set.
 * `search` = the name or the country code.
 */
export const shortlistOrganizationsQuery = paginationQuery.extend({
	games_id: uuid,
	search: searchParam.optional(),
	state: z.enum(SHORTLIST_ORG_STATES).optional(),
});
export type ShortlistOrganizationsQuery = z.infer<typeof shortlistOrganizationsQuery>;
export type ShortlistOrganizationsQueryInput = z.input<typeof shortlistOrganizationsQuery>;

/** By athlete: PRIMARY somewhere · RESERVE only · not shortlisted at all. */
export const SHORTLIST_ATHLETE_ROLE_FILTERS = ["primary", "reserve", "none"] as const;
export type ShortlistAthleteRoleFilter = (typeof SHORTLIST_ATHLETE_ROLE_FILTERS)[number];

export const SHORTLIST_ATHLETES_PAGE_SIZE_DEFAULT = 50;
export const SHORTLIST_SEARCH_MAX = 100;

/**
 * `GET /shortlist/athletes` — one organisation's APPROVED athletes across
 * every sport, keyset by (name, card id); the first page carries `total`.
 * `sport_id` = the card's sport (or one of its sports) is that one; `q` = the
 * name, or a card number in any printed form (`parseCardNumberQuery`).
 */
export const shortlistAthletesQuery = shortlistQuery.extend({
	sport_id: uuid.optional(),
	gender: z.enum(PERSON_GENDERS).optional(),
	role: z.enum(SHORTLIST_ATHLETE_ROLE_FILTERS).optional(),
	q: z.string().trim().min(1).max(SHORTLIST_SEARCH_MAX).optional(),
	cursor: z.string().trim().min(1).max(500).optional(),
	limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(SHORTLIST_ATHLETES_PAGE_SIZE_DEFAULT),
});
export type ShortlistAthletesQuery = z.infer<typeof shortlistAthletesQuery>;
export type ShortlistAthletesQueryInput = z.input<typeof shortlistAthletesQuery>;

// ── Bodies ──────────────────────────────────────────────────────────────────

const pairKey = (r: { ac_card_id: string; event_id: string }) => `${r.ac_card_id.toLowerCase()}|${r.event_id.toLowerCase()}`;

/** A best record: the mark in the canonical unit of the event's `result_type` (`parseMark`). */
const bestMarkField = z
	.number()
	.min(0)
	.lt(MARK_LIMIT)
	.refine((v) => Number(v.toFixed(MARK_DECIMALS)) === v, `At most ${MARK_DECIMALS} decimals`);
const isoDateField = z
	.string()
	.trim()
	.regex(/^\d{4}-\d{2}-\d{2}$/, "A date like 2026-05-31")
	.refine((v) => {
		const d = new Date(`${v}T00:00:00Z`);
		return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(v);
	}, "Not a real date");

/**
 * `PUT /shortlist/entries` — set or clear (`role: null`) up to
 * SHORTLIST_ENTRIES_BATCH_MAX (card, event) pairs, one transaction. Rows are
 * judged one by one: the refused ones come back with a reason, the rest are
 * written. A pair may appear once.
 *
 * The best record (0062) is ONE thing: a row that carries any of
 * `best_mark`, `best_mark_date`, `best_mark_competition` REPLACES the record
 * whole (one of the three left out is cleared); a row with none of them
 * keeps the stored record. The mark is in the canonical unit of the event's
 * `result_type` (`parseMark`).
 */
export const shortlistEntriesBody = z.object({
	entries: z
		.array(
			z.object({
				ac_card_id: uuid,
				event_id: uuid,
				role: z.enum(SHORTLIST_ROLES).nullable(),
				best_mark: bestMarkField.nullable().optional(),
				best_mark_date: isoDateField.nullable().optional(),
				best_mark_competition: z.string().trim().min(1).max(BEST_MARK_COMPETITION_MAX).nullable().optional(),
			}),
		)
		.min(1)
		.max(SHORTLIST_ENTRIES_BATCH_MAX)
		.refine((rows) => new Set(rows.map(pairKey)).size === rows.length, "Each athlete and event may appear once"),
});
export type ShortlistEntriesBody = z.input<typeof shortlistEntriesBody>;

// ── Responses ───────────────────────────────────────────────────────────────

/**
 * One organisation's shortlist deadline (the SHORTLIST windows matching the
 * games and the organisation or every organisation), by the database clock.
 * The API reads it with the SAME builder the writing statement gates on, so
 * the tile and the lock cannot disagree.
 */
export interface ShortlistDeadline {
	/** A SHORTLIST window matches. False = open, no deadline. */
	applies: boolean;
	/** Scoped logins may write now: no window matches, or one covers now. */
	open: boolean;
	/** The deadline: the latest `closes_at` among the matching windows (null when none match). */
	closes_at: string | null;
	/** Not open: the earliest window still to open (null when none). */
	next_opens_at: string | null;
}

/** The display state of a deadline (`SHORTLIST_DEADLINE_STATES`). */
export function shortlistDeadlineState(d: ShortlistDeadline): ShortlistDeadlineState {
	if (!d.applies) return "none";
	if (d.open) return "open";
	return d.next_opens_at === null ? "closed" : "upcoming";
}

export interface ShortlistSportRef {
	sport_id: string;
	code: string;
	name: string;
}

/** One sport card of `GET /shortlist/sports`: counts over APPROVED athlete cards of the organisation in scope. */
export interface ShortlistSportItem {
	sport: ShortlistSportRef;
	/** APPROVED `Aa` cards of the organisation whose sport(s) include this sport. */
	athlete_count: number;
	event_count: number;
	primary_count: number;
	reserve_count: number;
}

export interface ShortlistSportsResponse {
	games_id: string;
	responsible_organization_id: string;
	/** The organisation's shortlist deadline (one for every sport). */
	deadline: ShortlistDeadline;
	items: ShortlistSportItem[];
}

export interface ShortlistQuotaUsage {
	/** `shortlistPrimaryLimit`; null = no limit. */
	limit: number | null;
	primary_used: number;
	reserve_used: number;
}

export interface ShortlistEventView {
	event_id: string;
	code: string;
	name: string;
	gender: EventGender;
	competitor_type: CompetitorType;
	team_size_min: number | null;
	team_size_max: number | null;
	quota: ShortlistQuotaUsage;
	/** 0062: entries that compete per organisation (null = no limit) — shown beside the shortlist quota. */
	max_entries_per_noc: number | null;
	/** 0062: the event's own shortlist quota (null = `quota.limit` falls back to the entry-quota rule). */
	max_shortlist_per_noc: number | null;
	/** 0062: what a best record measures (its canonical unit, `parseMark`) and which way is better — always, standard or not. */
	result_type: ResultType;
	lower_is_better: boolean;
	/** 0062: the event's qualifying standard (a scoped PRIMARY needs a best record that meets it); null = none. */
	qualifying: EventQualifyingStandard | null;
	/**
	 * M7b. The organisation's EBN number for this event (athletes, or teams
	 * for a TEAM event); null = none declared. Display only — it never
	 * changes `quota.limit` (`shortlistPrimaryLimit`).
	 */
	declared_entries: number | null;
}

export interface ShortlistAthleteEntry {
	event_id: string;
	role: ShortlistRole;
	/** The person holds an OVR entry for this event: the row cannot be cleared (SHORTLIST_ENTERED). */
	entered: boolean;
	/** 0062: the athlete's best record for the event, in the canonical unit of its `result_type` (`formatMark`). */
	best_mark: number | null;
	/** `YYYY-MM-DD`. */
	best_mark_date: string | null;
	best_mark_competition: string | null;
	/** `meetsStandard(best_mark, qualifying.mark, lower_is_better)`, computed on read: null = the event has no standard. */
	meets_standard: boolean | null;
}

export interface ShortlistAthlete {
	ac_card_id: string;
	people_id: string;
	printed_name: string;
	gender: PersonGender | null;
	registration_no: number;
	registration_no_display: string;
	/** `accreditation_no` (= `registration_no` once approved); null only if the card was never approved. */
	accreditation_no: number | null;
	/** `formatAccreditationNo` — what the GMS shows for an approved card; fall back to `registration_no_display` when null. */
	accreditation_no_display: string | null;
	photo_url: string | null;
	entries: ShortlistAthleteEntry[];
}

/** `GET /shortlist/sports/:sportId` — one statement: the sport's events × the athletes × their entries. */
export interface ShortlistSportView {
	games_id: string;
	responsible_organization_id: string;
	sport: ShortlistSportRef;
	deadline: ShortlistDeadline;
	/** The caller may change rows now (scope + permission, and the deadline open — admins: always). */
	can_edit: boolean;
	events: ShortlistEventView[];
	athletes: ShortlistAthlete[];
}

export interface ShortlistEntryRefusal {
	ac_card_id: string;
	event_id: string;
	reason: ShortlistRefusalReason;
	/** reason = not_eligible. */
	ineligible?: ShortlistIneligibleReason;
	/** reason = quota_full. */
	limit?: number;
	used?: number;
	/** reason = below_standard: the standard and the record compared (canonical unit). */
	standard?: number;
	mark?: number;
}

export interface ShortlistEntriesResult {
	/** Rows inserted, changed or cleared. */
	updated: number;
	/** Rows that already said that (nothing written, no audit row). */
	unchanged: number;
	refused: ShortlistEntryRefusal[];
}

/** One NOC of `GET /shortlist/organizations`. Counted as the sport cards count (APPROVED athlete cards in the caller's scope). */
export interface ShortlistOrganizationRow {
	organization: { id: string; name: string; code: string | null };
	state: ShortlistOrgState;
	athlete_count: number;
	/** Listed sports (athletes or entries). */
	sport_count: number;
	deadline: ShortlistDeadline;
	primary_count: number;
	reserve_count: number;
}

export interface ShortlistOrganizationsResponse {
	items: ShortlistOrganizationRow[];
	page: number;
	page_size: number;
	total: number;
	total_pages: number;
	/** Per state over the same search WITHOUT the state filter, so a chip equals its list. */
	state_counts: Array<{ state: ShortlistOrgState; count: number }>;
}

export interface ShortlistAthleteRowEntry {
	event_id: string;
	code: string;
	name: string;
	sport_id: string;
	role: ShortlistRole;
}

/** One athlete of `GET /shortlist/athletes`. */
export interface ShortlistAthleteRow {
	ac_card_id: string;
	people_id: string;
	printed_name: string;
	gender: PersonGender | null;
	registration_no_display: string;
	accreditation_no_display: string | null;
	photo_url: string | null;
	/** The card's sport(s), in programme order. */
	sports: ShortlistSportRef[];
	/** Every shortlist row of the card, in programme order. */
	entries: ShortlistAthleteRowEntry[];
}

export interface ShortlistAthletesPage {
	items: ShortlistAthleteRow[];
	next_cursor: string | null;
	/** First page only (null on later pages). */
	total: number | null;
}
